// src/pages/AgentDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../../utils/api';

// PrimeReact + Chart.js
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';

// 🎨 Orange Theme Colors
const BRAND_ORANGE = '#F89423';
const BRAND_ORANGE_DARK = '#ea7c1f';
const BRAND_ORANGE_LIGHT = '#fef3e2';

const CHART_COLORS = {
  primary: BRAND_ORANGE,
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  gray: '#6b7280'
};

// ✅✅✅ AGENT MOCK DATA - Following AdminDashboard Pattern ✅✅✅
const MOCK_AGENT_DATA = {
  // Stats
  totalSales: 47,
  pendingCommissions: 125000,
  activePolicies: 23,
  conversionRate: 68,
  monthlyEarnings: 285000,
  
  // Charts
  earnings: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    values: [120000, 185000, 210000, 245000, 265000, 285000]
  },
  sales: {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    values: [8, 12, 15, 12]
  },
  
  // Recent Sales Table
  recentSales: [
    { id: 1, customer: 'Adebayo Johnson', product: 'Seniors Plan', amount: 45000, status: 'Completed', date: '2024-01-15' },
    { id: 2, customer: 'Chioma Okafor', product: 'FlexiPlan Basic', amount: 28000, status: 'Pending', date: '2024-01-14' },
    { id: 3, customer: 'Ibrahim Musa', product: 'Higher Plus', amount: 67000, status: 'Completed', date: '2024-01-13' },
    { id: 4, customer: 'Grace Eze', product: 'Seniors Prime', amount: 52000, status: 'Processing', date: '2024-01-12' },
    { id: 5, customer: 'Tunde Bakare', product: 'FlexiPlan Premium', amount: 89000, status: 'Completed', date: '2024-01-11' },
    { id: 6, customer: 'Fatima Abdullahi', product: 'Higher Plan', amount: 34000, status: 'Pending', date: '2024-01-10' },
    { id: 7, customer: 'Emeka Nwosu', product: 'Seniors Plan', amount: 41000, status: 'Completed', date: '2024-01-09' },
    { id: 8, customer: 'Aisha Mohammed', product: 'FlexiPlan Standard', amount: 56000, status: 'Completed', date: '2024-01-08' },
    { id: 9, customer: 'Chidi Okonkwo', product: 'Seniors Plus', amount: 38000, status: 'Processing', date: '2024-01-07' },
    { id: 10, customer: 'Blessing Ade', product: 'Higher Plus', amount: 72000, status: 'Completed', date: '2024-01-06' }
  ],
  
  // Agent Profile
  agentProfile: {
    fullName: 'Oonyebuchu Okofu',
    agentType: 'Direct Agent',
    phoneNumber: '+234 801 234 5678',
    joinedDate: '2023-06-15',
    companyId: 46,
    isActive: true
  }
};

// ✅ Helper: Get agent session
const getAgentSession = () => {
  try {
    return JSON.parse(localStorage.getItem('agentSession') || '{}');
  } catch {
    return {};
  }
};

function AgentDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data State
  const [agentData, setAgentData] = useState({});
  const [stats, setStats] = useState({
    totalSales: 0,
    pendingCommissions: 0,
    activePolicies: 0,
    conversionRate: 0,
    monthlyEarnings: 0
  });
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [recentSales, setRecentSales] = useState([]);
  const [salesFilter, setSalesFilter] = useState({
    status: 'all',
    dateRange: 'month',
    search: ''
  });

  // ✅ Load mock data (replace with API call in production)
  const loadAgentData = useCallback(() => {
    try {
      setLoading(true);
      setTimeout(() => {
        setAgentData(MOCK_AGENT_DATA.agentProfile);
        setStats({
          totalSales: MOCK_AGENT_DATA.totalSales,
          pendingCommissions: MOCK_AGENT_DATA.pendingCommissions,
          activePolicies: MOCK_AGENT_DATA.activePolicies,
          conversionRate: MOCK_AGENT_DATA.conversionRate,
          monthlyEarnings: MOCK_AGENT_DATA.monthlyEarnings
        });
        setRecentSales(MOCK_AGENT_DATA.recentSales);
        initializeCharts(MOCK_AGENT_DATA);
        setLoading(false);
        console.log('✅ Agent data loaded');
      }, 800);
    } catch (error) {
      console.error('Failed to load agent data', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  }, []);

  // ✅ Initialize Charts - Following AdminDashboard Pattern
  const initializeCharts = (data = {}) => {
    // Earnings Chart (Line)
    const earningsData = {
      labels: data.earnings?.labels || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      datasets: [{
        label: 'Earnings (₦)',
        data: data.earnings?.values || [120000, 185000, 210000, 245000, 265000, 285000],
        borderColor: BRAND_ORANGE,
        backgroundColor: 'rgba(248, 148, 35, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: BRAND_ORANGE,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4
      }]
    };

    const earningsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1f2937',
          titleColor: '#fff',
          bodyColor: '#fff',
          callbacks: { label: (ctx) => `₦${ctx.parsed.y.toLocaleString()}` }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(0,0,0,0.05)' },
          ticks: { callback: (value) => `₦${(value / 1000).toFixed(0)}k`, color: CHART_COLORS.gray }
        },
        x: { grid: { display: false }, ticks: { color: CHART_COLORS.gray } }
      }
    };

    // Sales Chart (Bar)
    const salesData = {
      labels: data.sales?.labels || ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      datasets: [{
        label: 'Sales',
        data: data.sales?.values || [8, 12, 15, 12],
        backgroundColor: CHART_COLORS.success,
        borderColor: CHART_COLORS.success,
        borderWidth: 1,
        borderRadius: 6
      }]
    };

    const salesOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: CHART_COLORS.gray } },
        x: { grid: { display: false }, ticks: { color: CHART_COLORS.gray } }
      }
    };

    setChartData({ earnings: earningsData, sales: salesData });
    setChartOptions({ earnings: earningsOptions, sales: salesOptions });
  };

  // ✅ Check auth & load data on mount
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    
    const checkAgentAuth = () => {
      const session = getAgentSession();
      if (!session.agentId) {
        toast.error('Please login to continue');
        navigate('/agent-login', { replace: true });
        return false;
      }
      // Check expiry
      if (session.expiresAt && Date.now() > session.expiresAt) {
        localStorage.removeItem('agentSession');
        toast.warning('Session expired. Please login again.');
        navigate('/agent-login', { replace: true });
        return false;
      }
      return true;
    };

    if (checkAgentAuth()) {
      loadAgentData();
    }
  }, [navigate, loadAgentData]);

  // ✅ Toggle Dropdown (for sidebar)
  const toggleDropdown = (path) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const isExpanded = (path) => expandedItems.has(path);

  // ✅ Format Currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // ✅ Filter Recent Sales
  const filteredSales = useMemo(() => {
    let filtered = [...recentSales];
    
    if (salesFilter.status !== 'all') {
      filtered = filtered.filter(sale => sale.status?.toLowerCase() === salesFilter.status);
    }
    
    if (salesFilter.search) {
      const searchLower = salesFilter.search.toLowerCase();
      filtered = filtered.filter(sale =>
        sale.customer?.toLowerCase().includes(searchLower) ||
        sale.product?.toLowerCase().includes(searchLower) ||
        sale.id?.toString().includes(searchLower)
      );
    }
    
    return filtered;
  }, [recentSales, salesFilter]);

  // ✅ Handle Logout
  const handleLogout = (e) => {
    e?.preventDefault();
    localStorage.removeItem('agentSession');
    localStorage.removeItem('agentRememberedEmail');
    toast.success('Logged out successfully');
    navigate('/agent-login', { replace: true });
  };

  // ✅ Nav Items for Agent Sidebar
  const navItems = useMemo(() => {
    const currentPath = location.pathname;
    return [
      {
        section: 'MAIN',
        items: [
          { name: 'Dashboard', icon: 'fa-chart-line', path: '/agent-dashboard', active: currentPath === '/agent-dashboard' }
        ]
      },
      
     
      {
        section: 'Plan',
        items: [
          { name: 'Plan link', icon: 'fa-user', path: '/agent-links ', active: currentPath === 'agent-links ' }
         
        ]
      }
    ];
  }, [location.pathname]);

  // ✅ Status Badge Template for Sales Table
  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status?.toLowerCase()) {
        case 'completed': return 'success';
        case 'pending': return 'warning';
        case 'processing': return 'info';
        case 'cancelled': return 'danger';
        default: return null;
      }
    };
    return (
      <Tag value={rowData.status || 'N/A'} severity={getSeverity(rowData.status)} className="fw-semibold" />
    );
  };

  // ✅ Sparkline Chart Component (for stats cards)
  const SparklineChart = ({ data, color = BRAND_ORANGE, height = 40 }) => {
    const sparkData = {
      labels: data.map((_, i) => i),
      datasets: [{
        data,
        borderColor: color,
        backgroundColor: 'transparent',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      }]
    };
    const sparkOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false, beginAtZero: true } }
    };
    return (
      <div style={{ height: `${height}px` }}>
        <Chart type="line" data={sparkData} options={sparkOptions} />
      </div>
    );
  };

  // ✅ Loading State
  if (loading && !stats.totalSales) {
    return (
      <div className="agent-dashboard-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading agent dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/agent-dashboard" className="brand-logo">
            <img src="images/fsl_logo.png" alt="Logo" style={{ height: '45px' }} />
            <span style={{ marginLeft: '8px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Agent Portal</span>
          </Link>
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((section, sectionIdx) => (
            <div key={sectionIdx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx} className="nav-item">
                    {item.hasDropdown ? (
                      <>
                        <button
                          className={`nav-link ${item.active ? 'active' : ''} ${isExpanded(item.path) ? 'expanded' : ''}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown(item.path); }}
                          type="button"
                          aria-expanded={isExpanded(item.path)}
                        >
                          <i className={`fas ${item.icon} me-2`}></i>
                          <span>{item.name}</span>
                          <i className={`fas fa-chevron-down ms-auto dropdown-toggle ${isExpanded(item.path) ? 'rotated' : ''}`}></i>
                        </button>
                        <div className={`dropdown-menu ${isExpanded(item.path) ? 'show' : ''}`}>
                          <ul className="dropdown-list">
                            {item.children?.map((child, childIdx) => (
                              <li key={childIdx}>
                                <Link to={child.path} className={`dropdown-item ${child.active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                  <i className={`fas ${child.icon} me-2`}></i>
                                  <span>{child.name}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <Link to={item.path} className={`nav-link ${item.active ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <i className={`fas ${item.icon} me-2`}></i>
                        <span>{item.name}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <li><Link to="/agent/profile" className="nav-link"><i className="fas fa-user me-2"></i>Profile</Link></li>
              <li><a href="/" className="nav-link" onClick={handleLogout}><i className="fas fa-sign-out-alt me-2"></i>Logout</a></li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>Agent Dashboard</h4>
              <small className="text-muted">Welcome, {agentData.fullName || 'Agent'}</small>
            </div>
          </div>
          <div className="header-right">
            <Button 
              label="New Sale" 
              icon="fas fa-plus" 
              onClick={() => navigate('/agent/sales/new')}
              style={{ backgroundColor: BRAND_ORANGE, borderColor: BRAND_ORANGE }}
              size="small"
            />
            <div className="user-avatar" style={{ backgroundColor: BRAND_ORANGE }} title={agentData.fullName}>
              {agentData.fullName?.charAt(0) || 'A'}
            </div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          {/* TOP METRICS - Following AdminDashboard Pattern */}
          <div className="row mb-4" data-aos="fade-up">
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Total Sales</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.totalSales}</h3>
                    </div>
                    <Tag value="📊" severity="info" />
                  </div>
                  <SparklineChart data={[32, 38, 41, 44, 46, stats.totalSales]} />
                  <small className="text-muted">All time</small>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Pending Commissions</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{formatCurrency(stats.pendingCommissions)}</h3>
                    </div>
                    <Tag value="⏳" severity="warning" />
                  </div>
                  <SparklineChart data={[85000, 98000, 110000, 118000, 122000, stats.pendingCommissions]} color={CHART_COLORS.warning} />
                  <small className="text-muted">Awaiting approval</small>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Active Policies</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.activePolicies}</h3>
                    </div>
                    <Tag value="✅" severity="success" />
                  </div>
                  <SparklineChart data={[15, 17, 19, 21, 22, stats.activePolicies]} color={CHART_COLORS.success} />
                  <small className="text-muted">Under management</small>
                </div>
              </Card>
            </div>
            <div className="col-lg-3 col-md-6 mb-3">
              <Card className="stats-card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <p className="text-muted small mb-1">Conversion Rate</p>
                      <h3 className="mb-0 fw-bold" style={{ color: '#1f2937' }}>{stats.conversionRate}%</h3>
                    </div>
                    <Tag value={stats.conversionRate >= 70 ? 'Excellent' : stats.conversionRate >= 50 ? 'Good' : 'Improving'} severity={stats.conversionRate >= 70 ? 'success' : stats.conversionRate >= 50 ? 'warning' : 'danger'} />
                  </div>
                  <ProgressBar value={stats.conversionRate} style={{ height: '8px' }} />
                  <small className="text-muted">Leads to sales</small>
                </div>
              </Card>
            </div>
          </div>

          {/* Charts Grid - Following AdminDashboard Pattern */}
          <div className="row mb-4">
            <div className="col-lg-8 mb-4" data-aos="fade-up">
              <Card className="border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-semibold">Monthly Earnings</h5>
                    <select className="form-select form-select-sm w-auto">
                      <option>Last 6 Months</option>
                      <option>This Year</option>
                      <option>All Time</option>
                    </select>
                  </div>
                </div>
                <div className="card-body p-0 px-4 pb-4" style={{ height: '320px' }}>
                  {chartData.earnings && <Chart type="line" data={chartData.earnings} options={chartOptions.earnings} className="w-100 h-100" />}
                </div>
              </Card>
            </div>
            <div className="col-lg-4 mb-4" data-aos="fade-up" data-aos-delay="100">
              <Card className="border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <h5 className="mb-0 fw-semibold">Weekly Sales</h5>
                </div>
                <div className="card-body p-0 px-4 pb-4" style={{ height: '320px' }}>
                  {chartData.sales && <Chart type="bar" data={chartData.sales} options={chartOptions.sales} className="w-100 h-100" />}
                </div>
              </Card>
            </div>
          </div>

          {/* Recent Sales Table - Following AdminDashboard Pattern */}
          <div className="row mb-4" data-aos="fade-up">
            <div className="col-12">
              <Card className="border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <h5 className="mb-0 fw-semibold">Recent Sales</h5>
                    <div className="d-flex gap-2 flex-wrap">
                      <InputText 
                        placeholder="Search by customer or product..." 
                        value={salesFilter.search} 
                        onChange={(e) => setSalesFilter(prev => ({ ...prev, search: e.target.value }))} 
                        className="w-200" 
                        style={{ maxWidth: '250px' }} 
                      />
                      <Dropdown 
                        value={salesFilter.status} 
                        options={[
                          { label: 'All Status', value: 'all' }, 
                          { label: 'Completed', value: 'completed' }, 
                          { label: 'Pending', value: 'pending' }, 
                          { label: 'Processing', value: 'processing' }
                        ]} 
                        onChange={(e) => setSalesFilter(prev => ({ ...prev, status: e.value }))} 
                        placeholder="Filter by status" 
                        className="w-150" 
                      />
                      <Button label="Export" icon="pi pi-download" severity="secondary" size="small" onClick={() => toast.info('Export feature coming soon!')} />
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <DataTable 
                    value={filteredSales} 
                    paginator 
                    rows={10} 
                    rowsPerPageOptions={[5, 10, 25, 50]} 
                    tableStyle={{ minWidth: '800px' }} 
                    emptyMessage="No sales data found" 
                    className="p-datatable-sm"
                  >
                    <Column field="id" header="ID" sortable style={{ width: '80px' }} />
                    <Column field="customer" header="Customer" sortable />
                    <Column field="product" header="Product" sortable />
                    <Column field="amount" header="Amount" sortable body={(rowData) => formatCurrency(rowData.amount)} />
                    <Column field="status" header="Status" sortable body={statusBodyTemplate} />
                    <Column field="date" header="Date" sortable />
                    <Column header="Actions" body={(rowData) => (
                      <Button icon="pi pi-eye" className="p-button-text p-button-sm" onClick={() => toast.info(`Viewing sale #${rowData.id}`)} tooltip="View Details" />
                    )} style={{ width: '80px' }} />
                  </DataTable>
                </div>
              </Card>
            </div>
          </div>

          {/* Quick Actions + Agent Stats - Following AdminDashboard Pattern */}
          <div className="row">
            <div className="col-lg-8 mb-4">
              <Card className="border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <h5 className="mb-0 fw-semibold">Quick Actions</h5>
                </div>
                <div className="card-body px-4 pb-4">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Button label="New Sale" icon="fas fa-plus" className="w-100" severity="secondary" onClick={() => navigate('/agent/sales/new')} />
                    </div>
                    <div className="col-md-4">
                      <Button label="Add Customer" icon="fas fa-user-plus" className="w-100" severity="info" onClick={() => navigate('/agent/customers/add')} />
                    </div>
                    <div className="col-md-4">
                      <Button label="View Commissions" icon="fas fa-coins" className="w-100" severity="warning" onClick={() => navigate('/agent/commissions')} />
                    </div>
                    <div className="col-md-4">
                      <Button label="Download Report" icon="fas fa-download" className="w-100" severity="success" onClick={() => toast.info('Report generation coming soon!')} />
                    </div>
                    <div className="col-md-4">
                      <Button label="Contact Support" icon="fas fa-headset" className="w-100" severity="danger" onClick={() => navigate('/agent/support')} />
                    </div>
                    <div className="col-md-4">
                      <Button label="Update Profile" icon="fas fa-user-edit" className="w-100" severity="secondary" onClick={() => navigate('/agent/profile')} />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            <div className="col-lg-4 mb-4">
              <Card className="border-0 shadow-sm">
                <div className="card-header bg-white border-0 py-3 px-4">
                  <h6 className="mb-0 fw-semibold">Agent Performance</h6>
                </div>
                <div className="card-body px-4 pb-4">
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Monthly Target</span>
                      <span className="small fw-semibold">₦500,000</span>
                    </div>
                    <ProgressBar value={Math.min((stats.monthlyEarnings / 500000) * 100, 100)} color="custom-orange" style={{ height: '8px' }} />
                    <small className="text-muted d-block mt-1">{formatCurrency(stats.monthlyEarnings)} earned this month</small>
                  </div>
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Commission Rate</span>
                      <span className="small fw-semibold">12.5%</span>
                    </div>
                    <ProgressBar value={12.5} color="custom-green" style={{ height: '8px' }} />
                    <small className="text-muted d-block mt-1">Average commission per sale</small>
                  </div>
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Customer Satisfaction</span>
                      <span className="small fw-semibold">4.8/5</span>
                    </div>
                    <ProgressBar value={96} color="custom-blue" style={{ height: '8px' }} />
                    <small className="text-muted d-block mt-1">Based on 23 reviews</small>
                  </div>
                  <div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="small text-muted">Response Time</span>
                      <span className="small fw-semibold">&lt; 2 hours</span>
                    </div>
                    <ProgressBar value={92} color="custom-warning" style={{ height: '8px' }} />
                    <small className="text-muted d-block mt-1">Average customer response</small>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Styles - Following AdminDashboard Pattern */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        /* Scrollbar */
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: ${BRAND_ORANGE}; border-radius: 10px; border: 2px solid #f1f1f1; }
        ::-webkit-scrollbar-thumb:hover { background: ${BRAND_ORANGE_DARK}; }
        * { scrollbar-width: thin; scrollbar-color: ${BRAND_ORANGE} #f1f1f1; }
        
        .agent-dashboard-page { display: flex; min-height: 100vh; background: #f8f9fa; }
        
        /* Sidebar */
        .sidebar {
          width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0;
          height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000;
          border-right: 1px solid #e5e7eb; border-left: 3px solid ${BRAND_ORANGE};
        }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: center; }
        .brand-logo { color: #333; font-size: 20px; font-weight: 700; text-decoration: none; display: flex; align-items: center; gap: 8px; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: ${BRAND_ORANGE}; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-item { margin-bottom: 2px; }
        
        /* Nav Link */
        .nav-link {
          display: flex; align-items: center; padding: 12px 20px; color: #374151; text-decoration: none;
          transition: all 0.3s ease; font-weight: 500; font-size: 14px; border-left: 3px solid transparent;
          background: none; border: none; width: 100%; text-align: left; cursor: pointer;
        }
        .nav-link i { width: 20px; margin-right: 12px; font-size: 16px; color: #6b7280; }
        .nav-link:hover { background-color: ${BRAND_ORANGE_LIGHT}; color: ${BRAND_ORANGE}; border-left-color: ${BRAND_ORANGE}; }
        .nav-link:hover i { color: ${BRAND_ORANGE}; }
        .nav-link.active { background-color: ${BRAND_ORANGE}; color: white; border-left-color: ${BRAND_ORANGE}; }
        .nav-link.active i { color: white; }
        
        /* Dropdown Button */
        .nav-link.has-dropdown, .nav-link.expanded { position: relative; padding-right: 35px; }
        .nav-link .dropdown-toggle {
          position: absolute; right: 15px; top: 50%; transform: translateY(-50%);
          font-size: 10px; color: #6b7280; transition: transform 0.3s ease; pointer-events: none;
        }
        .nav-link .dropdown-toggle.rotated { transform: translateY(-50%) rotate(180deg); }
        .nav-link.expanded .dropdown-toggle { color: ${BRAND_ORANGE}; }
        
        /* Dropdown Menu */
        .dropdown-menu {
          max-height: 0; overflow: hidden; transition: max-height 0.4s ease, opacity 0.4s ease;
          opacity: 0; background: #f8f9fa; border-radius: 0 0 8px 8px; margin: 0; padding: 0;
        }
        .dropdown-menu.show { max-height: 600px; opacity: 1; box-shadow: inset 0 2px 4px rgba(0,0,0,0.05); }
        .dropdown-list { list-style: none; padding: 0; margin: 0; }
        .dropdown-item {
          display: flex; align-items: center; padding: 10px 20px 10px 52px; color: #4b5563;
          text-decoration: none; font-size: 13px; transition: all 0.2s ease; border-left: 3px solid transparent;
        }
        .dropdown-item:hover { background-color: ${BRAND_ORANGE_LIGHT}; color: ${BRAND_ORANGE}; border-left-color: ${BRAND_ORANGE}; }
        .dropdown-item.active { background-color: ${BRAND_ORANGE}; color: white; border-left-color: ${BRAND_ORANGE}; }
        .dropdown-item .dropdown-icon { width: 16px; margin-right: 10px; font-size: 14px; color: #6b7280; }
        .dropdown-item:hover .dropdown-icon, .dropdown-item.active .dropdown-icon { color: inherit; }
        
        .sidebar-footer { margin-top: auto; padding: 20px; border-top: 1px solid #e5e7eb; }
        .sidebar-footer .nav-link { color: #ef4444; padding: 10px 20px; background: none; border: none; width: 100%; text-align: left; cursor: pointer; }
        .sidebar-footer .nav-link:hover { background-color: #fef2f2; }
        
        /* Main Content */
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; background: #f9fafb; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .sidebar-toggle { background: none; border: none; font-size: 20px; color: #374151; cursor: pointer; display: none; }
        .page-title { display: flex; flex-direction: column; color: #1f2937; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; font-size: 14px; }
        
        /* Content */
        .content-wrapper { padding: 30px; flex: 1; }
        .card { border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); background: white; border: none; }
        .card-header { border-bottom: 1px solid #f1f5f9 !important; }
        .stats-card { transition: transform 0.2s ease; }
        .stats-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important; }
        
        /* PrimeReact */
        .p-card { border-radius: 12px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important; }
        .p-progressbar { border-radius: 4px !important; overflow: hidden; }
        .p-progressbar .p-progressbar-value { transition: width 0.3s ease; }
        .p-progressbar.p-progressbar-custom-orange .p-progressbar-value { background: ${BRAND_ORANGE} !important; }
        .p-progressbar.p-progressbar-custom-green .p-progressbar-value { background: ${CHART_COLORS.success} !important; }
        .p-progressbar.p-progressbar-custom-purple .p-progressbar-value { background: ${CHART_COLORS.secondary} !important; }
        .p-progressbar.p-progressbar-custom-blue .p-progressbar-value { background: ${CHART_COLORS.info} !important; }
        .p-progressbar.p-progressbar-custom-warning .p-progressbar-value { background: ${CHART_COLORS.warning} !important; }
        .p-button { border-radius: 8px !important; font-weight: 500; }
        .p-button.p-button-secondary { background: #f3f4f6 !important; color: #374151 !important; border: 1px solid #d1d5db !important; }
        .p-button.p-button-secondary:hover { background: #e5e7eb !important; }
        .p-button.p-button-info { background: ${CHART_COLORS.info} !important; border-color: ${CHART_COLORS.info} !important; }
        .p-button.p-button-warning { background: ${CHART_COLORS.warning} !important; border-color: ${CHART_COLORS.warning} !important; }
        
        /* DataTable */
        .p-datatable .p-datatable-header { background: #f8f9fa !important; border-bottom: 1px solid #e5e7eb !important; padding: 1rem !important; }
        .p-datatable .p-datatable-thead > tr > th { background: #f8f9fa !important; color: #374151 !important; font-weight: 600 !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 0.5px !important; }
        .p-datatable .p-datatable-tbody > tr > td { padding: 0.75rem 1rem !important; font-size: 14px !important; }
        .p-datatable .p-paginator { padding: 1rem !important; background: #f8f9fa !important; }
        
        /* Responsive */
        @media (max-width: 992px) {
          .sidebar { transform: translateX(-100%); }
          .sidebar.sidebar-open { transform: translateX(0); }
          .main-content { margin-left: 0; }
          .sidebar-toggle { display: block; }
        }
        @media (max-width: 768px) {
          .content-wrapper { padding: 15px; }
          .top-header { padding: 15px; }
          .header-right .btn span { display: none; }
        }
      `}</style>
    </div>
  );
}

export default AgentDashboard;